<!-- src/views/posts/PostGridView.vue -->
<script setup>
import { ref, computed, onMounted } from 'vue'
import { usePostStore } from '@/stores/postStore'
import { useRouter } from 'vue-router'
import Layout from "@/layouts/main.vue"
import PageHeader from "@/components/page-header"
import PostPreview from '@/components/posts/PostPreview.vue'
import Swal from 'sweetalert2'

// Store et Router
const postStore = usePostStore()
const router = useRouter()

// State
const searchQuery = ref('')
const currentUser = ref(null)
const filter = ref('All') // 'All', 'Today', 'Yesterday', etc.

// Computed properties
const filteredPosts = computed(() => {
  let posts = postStore.posts

  // Filtre par recherche
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    posts = posts.filter(post =>
      post.title?.toLowerCase().includes(query) ||
      post.description?.toLowerCase().includes(query) ||
      post.employee?.person?.firstname?.toLowerCase().includes(query) ||
      post.employee?.person?.lastname?.toLowerCase().includes(query)
    )
  }

  // Filtre par date (selon le template)
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const last7Days = new Date(now)
  last7Days.setDate(last7Days.getDate() - 7)
  const last30Days = new Date(now)
  last30Days.setDate(last30Days.getDate() - 30)

  switch (filter.value) {
    case 'Today':
      posts = posts.filter(post => {
        if (!post.date) return false
        const postDate = new Date(post.date)
        return postDate.toDateString() === now.toDateString()
      })
      break
    case 'Yesterday':
      posts = posts.filter(post => {
        if (!post.date) return false
        const postDate = new Date(post.date)
        return postDate.toDateString() === yesterday.toDateString()
      })
      break
    case 'Last 7 Days':
      posts = posts.filter(post => {
        if (!post.date) return false
        const postDate = new Date(post.date)
        return postDate >= last7Days
      })
      break
    case 'Last 30 Days':
      posts = posts.filter(post => {
        if (!post.date) return false
        const postDate = new Date(post.date)
        return postDate >= last30Days
      })
      break
    case 'This Month':
      posts = posts.filter(post => {
        if (!post.date) return false
        const postDate = new Date(post.date)
        return postDate.getMonth() === now.getMonth() && postDate.getFullYear() === now.getFullYear()
      })
      break
    case 'Last Year':
      posts = posts.filter(post => {
        if (!post.date) return false
        const postDate = new Date(post.date)
        return postDate.getFullYear() === now.getFullYear() - 1
      })
      break
    // 'All' - pas de filtre
  }

  return posts
})

// Actions
const handleViewList = () => {
  router.push('/posts/list')
}

const handleAddPost = () => {
  router.push('/posts/create')
}

const handleEditPost = (post) => {
  router.push(`/posts/edit/${post.id}`)
}

const handleDeletePost = async (post) => {
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: `Do you really want to delete "${post.title}"?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Yes, delete it!'
  })

  if (result.isConfirmed) {
    try {
      await postStore.deletePost(post.id)
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Post has been deleted successfully'
      })
    } catch (error) {
      console.error('Error deleting post:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to delete post'
      })
    }
  }
}

const handlePublishPost = async (post) => {
  const result = await Swal.fire({
    title: 'Publish Post',
    text: `Do you want to publish "${post.title}"?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, publish it!',
    cancelButtonText: 'Cancel'
  })

  if (result.isConfirmed) {
    try {
      await postStore.publishPost(post.id)
      Swal.fire({
        icon: 'success',
        title: 'Published!',
        text: 'Post has been published successfully'
      })
    } catch (error) {
      console.error('Error publishing post:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to publish post'
      })
    }
  }
}

const handleViewPost = (post) => {
  router.push(`/posts/${post.id}`)
}

const handleSchedulePost = async (post) => {
  const { value: dateTime } = await Swal.fire({
    title: 'Schedule Post',
    input: 'datetime-local',
    inputLabel: 'Select publish date and time',
    inputValue: post.date ? new Date(post.date).toISOString().slice(0, 16) : '',
    showCancelButton: true,
    confirmButtonText: 'Schedule',
    cancelButtonText: 'Cancel'
  })

  if (dateTime) {
    try {
      await postStore.schedulePost(post.id, dateTime)
      Swal.fire({
        icon: 'success',
        title: 'Scheduled!',
        text: 'Post has been scheduled successfully'
      })
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to schedule post'
      })
    }
  }
}

const handleDuplicatePost = async (post) => {
  const result = await Swal.fire({
    title: 'Duplicate Post',
    text: `Do you want to duplicate "${post.title}"?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, duplicate it!',
    cancelButtonText: 'Cancel'
  })

  if (result.isConfirmed) {
    try {
      await postStore.duplicatePost(post.id)
      Swal.fire({
        icon: 'success',
        title: 'Duplicated!',
        text: 'Post has been duplicated successfully'
      })
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to duplicate post'
      })
    }
  }
}

// Helper pour obtenir le badge du type de média
const getMediaBadge = (mediaType) => {
  switch (mediaType) {
    case 'T': return 'text-secondary'
    case 'V': return 'text-danger'
    case 'A': return 'text-success'
    case 'I': return 'text-info'
    case 'L': return 'text-primary'
    case 'F': return 'text-warning'
    default: return 'text-secondary'
  }
}

// Helper pour obtenir l'icône du type de média
const getMediaIcon = (mediaType) => {
  switch (mediaType) {
    case 'T': return 'ri-file-text-line'
    case 'V': return 'ri-video-line'
    case 'A': return 'ri-music-line'
    case 'I': return 'ri-image-line'
    case 'L': return 'ri-link'
    case 'F': return 'ri-file-text-line'
    default: return 'ri-file-text-line'
  }
}

// Format de date simplifié
const formatSimpleDate = (dateString) => {
  if (!dateString) return 'Draft'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

// Pagination
const currentPage = ref(1)
const itemsPerPage = 8
const paginatedPosts = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage
  return filteredPosts.value.slice(start, end)
})

const totalPages = computed(() => {
  return Math.ceil(filteredPosts.value.length / itemsPerPage)
})

// Statistics
const stats = computed(() => {
  const now = new Date()
  const today = now.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  
  return {
    total: postStore.posts.length,
    published: postStore.posts.filter(p => p.date).length,
    drafts: postStore.posts.filter(p => !p.date).length,
    today: postStore.posts.filter(p => {
      if (!p.date) return false
      return new Date(p.date).toDateString() === today
    }).length,
    yesterday: postStore.posts.filter(p => {
      if (!p.date) return false
      return new Date(p.date).toDateString() === yesterday.toDateString()
    }).length
  }
})

// Initialisation
onMounted(async () => {
  // Récupérer l'utilisateur courant
  const userId = localStorage.getItem("user");
  if (userId) {
    try {
      currentUser.value = JSON.parse(userId);
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }
  
  try {
    await postStore.fetchPosts()
  } catch (error) {
    console.error('Error loading posts:', error)
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Failed to load posts'
    })
  }
})
</script>

<template>
  <Layout>
    <PageHeader title="Posts Grid View" pageTitle="Posts" />

    <!-- Statistics Cards -->
    <div class="row mb-4">
      <BCol lg="3" sm="6">
        <BCard no-body class="card-animate">
          <BCardBody class="py-4">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1 overflow-hidden">
                <p class="text-uppercase fw-medium text-muted text-truncate mb-3">Total Posts</p>
                <h5 class="fs-18 mb-0">{{ stats.total }}</h5>
              </div>
              <div class="flex-shrink-0">
                <i class="ri-file-text-line text-primary display-6"></i>
              </div>
            </div>
          </BCardBody>
        </BCard>
      </BCol>
      <BCol lg="3" sm="6">
        <BCard no-body class="card-animate">
          <BCardBody class="py-4">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1 overflow-hidden">
                <p class="text-uppercase fw-medium text-muted text-truncate mb-3">Published</p>
                <h5 class="fs-18 mb-0">{{ stats.published }}</h5>
              </div>
              <div class="flex-shrink-0">
                <i class="ri-send-plane-line text-success display-6"></i>
              </div>
            </div>
          </BCardBody>
        </BCard>
      </BCol>
      <BCol lg="3" sm="6">
        <BCard no-body class="card-animate">
          <BCardBody class="py-4">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1 overflow-hidden">
                <p class="text-uppercase fw-medium text-muted text-truncate mb-3">Drafts</p>
                <h5 class="fs-18 mb-0">{{ stats.drafts }}</h5>
              </div>
              <div class="flex-shrink-0">
                <i class="ri-draft-line text-warning display-6"></i>
              </div>
            </div>
          </BCardBody>
        </BCard>
      </BCol>
      <BCol lg="3" sm="6">
        <BCard no-body class="card-animate">
          <BCardBody class="py-4">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1 overflow-hidden">
                <p class="text-uppercase fw-medium text-muted text-truncate mb-3">Today</p>
                <h5 class="fs-18 mb-0">{{ stats.today }}</h5>
              </div>
              <div class="flex-shrink-0">
                <i class="ri-calendar-event-line text-info display-6"></i>
              </div>
            </div>
          </BCardBody>
        </BCard>
      </BCol>
    </div>

    <!-- Search and Filters -->
    <div class="row g-4 mb-3">
      <div class="col-sm-auto">
        <div>
          <BButton variant="success" @click="handleAddPost">
            <i class="ri-add-line align-bottom me-1"></i> Add New Post
          </BButton>
        </div>
      </div>
      <div class="col-sm">
        <BButton variant="primary" @click="handleViewList">
          <i class="ri-list-check align-bottom me-1"></i> Switch to List View
        </BButton>
      </div>
      <div class="col-sm">
        <div class="d-flex justify-content-sm-end gap-2">
          <div class="search-box ms-2">
            <input type="text" class="form-control" placeholder="Search posts..." v-model="searchQuery">
            <i class="ri-search-line search-icon"></i>
          </div>

          <select class="form-control w-md" v-model="filter">
            <option value="All">All Posts</option>
            <option value="Today">Today</option>
            <option value="Yesterday">Yesterday</option>
            <option value="Last 7 Days">Last 7 Days</option>
            <option value="Last 30 Days">Last 30 Days</option>
            <option value="This Month">This Month</option>
            <option value="Last Year">Last Year</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="postStore.loading && filteredPosts.length === 0" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">Loading posts...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredPosts.length === 0" class="text-center py-5">
      <div class="py-5">
        <i class="ri-file-text-line display-4 text-muted mb-4"></i>
        <h5 class="mt-3">No posts found</h5>
        <p class="text-muted mb-4">Try adjusting your filters or create a new post</p>
        <BButton variant="primary" @click="handleAddPost" size="lg">
          <i class="ri-add-fill align-bottom me-1"></i> Create Your First Post
        </BButton>
      </div>
    </div>

    <!-- Posts Grid -->
    <div class="row row-cols-1 row-cols-md-2 row-cols-xl-3 row-cols-xxl-4" v-else>
      <div class="col mb-4" v-for="post in paginatedPosts" :key="post.id">
        <div class="card overflow-hidden blog-grid-card h-100">
          <div class="position-relative overflow-hidden">
            <PostPreview :post="post" preview-size="medium" />
            
            <!-- Status Badge -->
            <span v-if="!post.date" class="badge bg-warning position-absolute top-0 end-0 m-3">
              <i class="ri-draft-line me-1"></i> Draft
            </span>
            <span v-else class="badge bg-success position-absolute top-0 end-0 m-3">
              <i class="ri-send-plane-line me-1"></i> Published
            </span>
            
            <!-- Media Type Badge -->
            <span
              :class="`badge ${getMediaBadge(post.media_type)} bg-${getMediaBadge(post.media_type).replace('text-', '')}-subtle position-absolute top-0 start-0 m-3`">
              <i :class="`${getMediaIcon(post.media_type)} me-1`"></i>
              {{ postStore.getMediaTypeLabel(post.media_type) }}
            </span>
          </div>
          
          <div class="card-body d-flex flex-column">
            <div>
              <h5 class="card-title">
                <a href="javascript:void(0);" @click="handleViewPost(post)" class="text-reset text-decoration-none">
                  {{ post.title }}
                </a>
              </h5>
              <p class="text-muted mb-3">
                {{ post.description.length > 120 ? post.description.substring(0, 120) + '...' : post.description }}
              </p>
            </div>

            <!-- Author and Date -->
            <div class="mt-auto">
              <div class="d-flex align-items-center mb-3">
                <div v-if="post.employee?.person?.photo_url" class="flex-shrink-0 me-2">
                  <img :src="post.employee.person.photo_url" alt="Author" class="rounded-circle avatar-xs">
                </div>
                <div v-else class="flex-shrink-0 me-2">
                  <div class="avatar-xs">
                    <div class="avatar-title bg-light rounded-circle">
                      <i class="ri-user-line"></i>
                    </div>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <small class="text-muted">
                    <i class="ri-user-3-line me-1"></i>
                    {{ post.employee?.person?.firstname }} {{ post.employee?.person?.lastname }}
                    | <i class="ri-calendar-event-line me-1 ms-2"></i>
                    {{ formatSimpleDate(post.date || post.created_at) }}
                  </small>
                </div>
              </div>

              <!-- Actions -->
              <div class="d-flex justify-content-between align-items-center border-top pt-3">
                <div>
                  <a href="javascript:void(0);" @click="handleViewPost(post)"
                    class="link link-primary text-decoration-underline link-offset-1">
                    Read More <i class="ri-arrow-right-line align-middle"></i>
                  </a>
                </div>
                
                <div class="dropdown" v-if="currentUser && (currentUser.id === post.employee?.person?.id || currentUser.role === 'admin')">
                  <button class="btn btn-light btn-sm btn-icon" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                    <i class="ri-more-2-fill"></i>
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end">
                    <li>
                      <a class="dropdown-item" href="javascript:void(0);" @click="handleViewPost(post)">
                        <i class="ri-eye-line me-2"></i> View
                      </a>
                    </li>
                    <li>
                      <a class="dropdown-item" href="javascript:void(0);" @click="handleEditPost(post)">
                        <i class="ri-edit-line me-2"></i> Edit
                      </a>
                    </li>
                    <li>
                      <a class="dropdown-item" href="javascript:void(0);" @click="handleDuplicatePost(post)">
                        <i class="ri-file-copy-line me-2"></i> Duplicate
                      </a>
                    </li>
                    <li v-if="!post.date">
                      <a class="dropdown-item" href="javascript:void(0);" @click="handlePublishPost(post)">
                        <i class="ri-send-plane-line me-2"></i> Publish
                      </a>
                    </li>
                    <li v-if="!post.date">
                      <a class="dropdown-item" href="javascript:void(0);" @click="handleSchedulePost(post)">
                        <i class="ri-calendar-schedule-line me-2"></i> Schedule
                      </a>
                    </li>
                    <li><hr class="dropdown-divider"></li>
                    <li>
                      <a class="dropdown-item text-danger" href="javascript:void(0);" @click="handleDeletePost(post)">
                        <i class="ri-delete-bin-line me-2"></i> Delete
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="row g-0 text-center text-sm-start align-items-center mb-4" v-if="totalPages > 1">
      <div class="col-sm-6">
        <div>
          <p class="mb-sm-0 text-muted">
            Showing <span class="fw-semibold">{{ Math.min((currentPage - 1) * itemsPerPage + 1, filteredPosts.length) }}</span>
            to <span class="fw-semibold">{{ Math.min(currentPage * itemsPerPage, filteredPosts.length) }}</span>
            of <span class="fw-semibold text-decoration-underline">{{ filteredPosts.length }}</span> entries
          </p>
        </div>
      </div>
      <div class="col-sm-6">
        <ul class="pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
          <li class="page-item" :class="{ disabled: currentPage <= 1 }">
            <a href="javascript:void(0);" class="page-link" @click="currentPage--">
              <i class="ri-arrow-left-s-line"></i>
            </a>
          </li>

          <li class="page-item" v-for="page in totalPages" :key="page" :class="{ active: page === currentPage }">
            <a href="javascript:void(0);" class="page-link" @click="currentPage = page">{{ page }}</a>
          </li>

          <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
            <a href="javascript:void(0);" class="page-link" @click="currentPage++">
              <i class="ri-arrow-right-s-line"></i>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.blog-grid-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid #e9ecef;
}

.blog-grid-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  border-color: #405189;
}

.blog-img {
  height: 200px;
  width: 100%;
  object-fit: cover;
}

.search-box {
  position: relative;
}

.search-box .search-icon {
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  color: #74788d;
}

.avatar-xs {
  width: 24px;
  height: 24px;
}

.card-animate {
  transition: transform 0.3s ease;
}

.card-animate:hover {
  transform: translateY(-5px);
}

/* Ensure consistent card heights */
.blog-grid-card .card-body {
  flex: 1;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .blog-grid-card .card-title {
    font-size: 1.1rem;
  }
  
  .blog-grid-card .text-muted {
    font-size: 0.9rem;
  }
}
</style>